import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
// routes
import { paths } from 'src/routes/paths';
// components
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import { APP_MODULES } from 'src/constants/app-modules';
import { MACHINE_API_ENABLED_ORG_IDS } from 'src/constants/misc';
import { useOrganizationContext } from 'src/context/organization';
import { useEffectiveRole } from 'src/hooks/use-effective-role';
import { useTenantConfig } from 'src/context/tenant';
import { CHECK_IF_VOTER_COMMUNICATIONS_METADATA_EXISTS } from 'src/graphql/voter_communications/queries';
import { useOrganizationVotingAreas } from 'src/hooks/useOrganizationVotingAreas';
import { isWebCallingEnabled } from 'src/utils/assigned-tasks/calling-access';

const icon = (name) => (
  <Iconify icon={name} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  user: icon('solar:user-bold'),
  adminUsers: icon('solar:shield-user-bold'),
  menuItem: icon('ic_menu_item'),
  analytics: icon('ic_analytics'),
  dashboard: icon('material-symbols:dashboard'),
  campaigns: icon('solar:flag-bold'),
  settings: icon('solar:settings-bold-duotone'),
  org: icon('heroicons:building-office-2'),
  planning: icon('solar:map-linear'),
  search: icon('carbon:search'),
  report: icon('mdi:report-box-multiple-outline'),
  questionBank: icon('wpf:survey'),
  tag: icon('solar:hashtag-bold'),
  team: icon('fluent:people-team-16-filled'),
  help: icon('mdi:help-box'),
  doNotContact: icon('solar:user-block-bold'),
  numberManagment: icon('solar:call-chat-rounded-outline'),
  finances: icon('solar:dollar-bold'),
  billing: icon('material-symbols:credit-card-clock-outline-rounded'),
  smsSurvey: icon('fluent-mdl2:questionnaire-mirrored'),
  voterCommunications: icon('mage:megaphone-a-fill'),
  downloads: icon('solar:download-outline'),
  uploads: icon('solar:upload-outline'),
  textReplies: icon('solar:video-frame-replace-bold-duotone'),
  messageTemplates: icon('mage:message-dots-plus'),
  policyAnalyst: icon('solar:document-text-bold'),
  trainingVideos: icon('solar:video-library-line-duotone'),
  dataDirector: icon('icon-park-outline:data-user'),
  machineApi: icon('material-symbols:settings-account-box-outline'),
  voicemailSetup: icon('solar:call-medicine-rounded-broken'),
  nearMe: icon('material-symbols:near-me-outline'),
  voicemailReplies: icon('material-symbols:phone-callback-rounded'),
  announcements: icon('mage:megaphone-a-fill'),
  assignedTasks: icon('solar:checklist-minimalistic-bold'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { user } = useAuthContext();
  const { selectedOrganizationId, selectedOrgData } = useOrganizationContext();
  const { state } = useOrganizationVotingAreas(selectedOrganizationId);
  // GL is an internal demo state with no legislation/vector data of its own;
  // reuse Idaho's data for it (mirrors useVoterCommunications).
  const effectiveState = state === 'GL' ? 'ID' : state;
  const { terminology, isLegislative } = useTenantConfig();

  // Navigation follows the per-org role, so it adapts when the selected org changes.
  // Same resolution the route guard and the API use — see useEffectiveRole.
  const userRole = useEffectiveRole();
  const isSuperAdmin = userRole === 'superadmin';

  const isMachineApiEnabled = MACHINE_API_ENABLED_ORG_IDS.includes(selectedOrganizationId);
  const isInternalOrg = selectedOrgData?.flg_prod === 'N';
  const isDataDirectorEnabled = [15187].includes(selectedOrganizationId);

  // Only the manager and legislative menus read this, so a volunteer never asks: the answer
  // would go unused, and Hasura role `user` cannot select voter_comm_metadata anyway.
  const { data: voterCommsMetadata } = useQuery(CHECK_IF_VOTER_COMMUNICATIONS_METADATA_EXISTS, {
    variables: {
      where: {
        state: {
          _eq: effectiveState,
        },
      },
    },
    skip: !state || userRole === 'user',
  });

  // The "Analytics Dashboards" group used to sit here. Every screen behind it was a Tableau
  // embed, and the Tableau subscription has ended, so the links are gone. The routes under
  // /superadmin/voter-analytics (and /superadmin/planning) are intentionally still registered —
  // removing them is a separate decision.
  const reportsNav = [
    {
      title: 'Reports',
      path: paths.reports.root,
      icon: ICONS.report,
      children: [
        {
          title: 'Performance Reports',
          path: paths.reports.activity,
        },
        {
          title: 'New Mover Reports',
          path: paths.reports.newMovers,
        },
        ...(!isLegislative && (isSuperAdmin || userRole === 'manager')
          ? [
              {
                title: 'District Leanings',
                path: paths.reports.districtLeanings,
              },
            ]
          : []),
        ...(!isLegislative
          ? [
              {
                title: 'Voter Contact Updates',
                path: paths.reports.voterContactUpdates,
              },
            ]
          : []),
        {
          title: 'Do Not Contact List',
          path: paths.reports.doNotContact,
        },
        ...(!isLegislative
          ? [
              {
                title: 'Absentee/Early Ballot Voters',
                path: paths.reports.absentee,
              },
            ]
          : []),
        ...(isLegislative
          ? []
          : [
              {
                title: 'Yard Signs',
                path: paths.reports.pinnedLocations,
              },
            ]),
        ...(isSuperAdmin
          ? [
              {
                title: 'Text Replies',
                path: paths.reports.replies,
              },
              {
                title: 'Voicemail Replies',
                path: paths.reports.voicemailReplies,
              },
              {
                title: 'Bad Numbers Report',
                path: paths.reports.bad_numbers,
              },
              {
                title: 'Potential Candidate Finder',
                path: paths.reports.potentialCandidates,
              },
              {
                title: 'Voter Outlook Report',
                path: paths.superadmin.automatedVoterReport,
              },
            ]
          : []),

        {
          title: 'Question Bank and Survey Results',
          path: paths.questionBank.questionBankAndSmsSurveyResults,
        },
      ],
    },
  ];

  const communicationsNav = {
    //subheader: 'COMMUNICATIONS MANAGER',
    path: '',
    items: [
      {
        title: 'Communications Assistant',
        path: paths.voterCommunications.general,
        icon: ICONS.voterCommunications,
      },
      ...(voterCommsMetadata?.aggregate?.aggregate?.count > 0
        ? [
            {
              title: 'Policy Analyst',
              path: paths.voterCommunications.policyAnalyst,
              icon: ICONS.policyAnalyst,
            },
          ]
        : []),
      ...(isDataDirectorEnabled
        ? [
            {
              title: 'Data Director',
              path: paths.voterCommunications.dataDirector,
              icon: ICONS.dataDirector,
            },
          ]
        : []),
    ],
  };

  const votersNav = {
    //subheader: 'VOTERS',
    path: '',
    items: [
      {
        title: `Search for a ${terminology.voter}`,
        // The rebuilt search is the default; the classic list is an opt-out from its screen.
        path: isLegislative ? paths.voters.legSearch : paths.voters.search,
        icon: ICONS.search,
        style: {},
      },
    ],
  };

  /**
   * National tracking console.
   *
   * Superadmin and national_viewer only, rendered inside those two branches
   * below — but note that hiding a nav item is NOT access control. The route is
   * independently wrapped in SuperAdminGuard, and /api/national enforces
   * roleCheck(['superadmin', 'national_viewer']) server-side. This entry is
   * purely discoverability.
   */
  // Standalone, with no subheader — same shape as commandCenterNav, which it
  // sits directly above. The section navigates by its own tab bar
  // (sections/national/national-tabs.js), so a group with one child would be a
  // category wrapping a single item and would give every screen two competing
  // sources of "where am I".
  const nationalNav = {
    path: '',
    items: [
      {
        title: 'National Dashboard',
        // Resolves to District Overview, which is also the first tab.
        path: paths.national.root,
        icon: ICONS.planning,
        // Without this the item matches /national exactly (nav-list passes
        // `hasChild || data.deep`, and this has no children), so it would go
        // dark the moment a tab pushed a child route.
        deep: true,
      },
    ],
  };

  const miscsNav = {
    subheader: 'Admin Tools',
    path: '',
    initiallyClosed: !isSuperAdmin,
    items: [
      {
        title: 'Manual Text Batches',
        path: paths.adminTools.manualTextMessages.root,
        icon: ICONS.planning,
      },
    ],
  };
  const billingNav = [
    {
      title: 'Billing',
      icon: ICONS.billing,
      path: paths.billing.root,
      children: [
        {
          title: 'Overview',
          path: paths.billing.root,
        },
        {
          title: 'Transactions',
          path: paths.billing.transactions,
        },
        ...(isSuperAdmin
          ? [
              {
                title: 'Task Billing',
                path: paths.billing.taskBills,
              },
              {
                title: 'Payment Reminders',
                path: paths.billing.paymentReminders,
              },
            ]
          : [
              {
                title: 'Billing Details',
                path: paths.billing.orgBillingDetails,
              },
            ]),
      ],
    },
  ];

  const manageAccountNav = {
    subheader: 'ORGANIZATION SETTINGS',
    initiallyClosed: !isSuperAdmin,
    path: '/settings',
    icon: ICONS.settings,
    items: [
      ...(!isSuperAdmin && !isLegislative && (isMachineApiEnabled || isInternalOrg)
        ? [
            {
              title: 'Machine API',
              path: paths.machineApiKeys.root,
              icon: ICONS.machineApi,
            },
          ]
        : []),
      ...(!isSuperAdmin
        ? [
            {
              title: 'Answering Machine Setup',
              path: paths.voicemailSetup.root,
              icon: ICONS.voicemailSetup,
            },
          ]
        : []),
      // Canvassing only. A legislative tenant has no door-knocking volunteers, so the
      // screen would offer settings for a feature its users never see.
      ...(!isLegislative
        ? [
            {
              title: 'Near Me Settings',
              path: paths.nearMeSettings.root,
              icon: ICONS.nearMe,
            },
          ]
        : []),
      {
        title: 'User Management',
        path: paths.users.root,
        icon: ICONS.user,
      },
      ...(isSuperAdmin
        ? [
            {
              title: 'Admin Users',
              path: paths.superadmin.adminUsers,
              icon: ICONS.adminUsers,
            },
          ]
        : []),
      {
        title: 'Team Management',
        path: paths.teams.root,
        icon: ICONS.team,
      },
      {
        title: 'Tag Management',
        path: paths.dashboard.tags,
        icon: ICONS.tag,
      },
      {
        title: 'Downloads',
        path: paths.downloads.root,
        icon: ICONS.downloads,
      },
      {
        title: 'Uploads',
        path: paths.uploads.root,
        icon: ICONS.uploads,
      },
      {
        title: 'Question Banks and Surveys',
        path: paths.questionBank.root,
        icon: ICONS.questionBank,
      },
      {
        title: 'Message Templates',
        path: paths.messageTemplates.root,
        icon: ICONS.messageTemplates,
      },
      ...(isSuperAdmin
        ? [
            {
              title: 'Announcements',
              path: paths.superadmin.announcements,
              icon: ICONS.announcements,
            },
          ]
        : []),
      ...(!isLegislative ? billingNav : []),
    ],
  };

  const reportsAndDashboardsNav = {
    subheader: 'Reports and Dashboards',
    initiallyClosed: true,
    items: [...reportsNav[0].children],
  };

  const financesNav = {
    //subheader: 'Campaign Finances',
    path: '',
    items: [
      {
        title: 'Campaign Finances',
        path: paths.finances.root,
        icon: ICONS.finances,
        style: {},
      },
    ],
  };

  const commandCenterNav = {
    path: '',
    items: [
      {
        title: 'Command Center',
        path: paths.dashboard.root,
        icon: ICONS.dashboard,
        deep: true,
      },
    ],
  };

  const assignedTasksNav = {
    path: '',
    items: [
      {
        title: 'Assigned Tasks',
        path: paths.assignedTasks.root,
        icon: ICONS.assignedTasks,
      },
    ],
  };
  // Only for organizations listed in WEB_CALLING_ENABLED_ORG_IDS.
  const callingNav = isWebCallingEnabled(selectedOrganizationId) ? [assignedTasksNav] : [];

  return useMemo(() => {
    if (userRole === 'superadmin') {
      return [
        nationalNav,
        commandCenterNav,
        votersNav,
        ...(isLegislative ? [] : [financesNav]),
        {
          subheader: 'ORGANIZATIONS',
          items: [
            {
              title: 'Organizations  Overview',
              path: paths.superadmin.orgs,
              icon: ICONS.dashboard,
            },
            {
              title: 'Manage Phone Numbers',
              path: paths.superadmin.number_managment,
              icon: ICONS.numberManagment,
            },

            // ...reportsNav,
          ],
        },
        reportsAndDashboardsNav,
        //communicationsNav,
        //votersNav,
        miscsNav,
        manageAccountNav,
      ];
    }

    if (userRole === 'manager') {
      return [
        ...callingNav,
        votersNav,
        billingNav,
        ...(user?.users_module_permissions?.some(
          (permission) =>
            permission?.organization_id === selectedOrganizationId &&
            permission?.user_module_permissions_app_module?.module === APP_MODULES.CAMPAIGN_FINANCES
        )
          ? [financesNav]
          : []),
        communicationsNav,

        {
          subheader: 'CAMPAIGNS',
          items: [
            {
              title: 'Working Campaigns',
              path: paths.dashboard.campaigns,
              icon: ICONS.campaigns,
            },
            ...(!isSuperAdmin
              ? [
                  {
                    title: 'Text Replies',
                    path: paths.reports.replies,
                    icon: ICONS.textReplies,
                  },
                  {
                    title: 'Voicemail Replies',
                    path: paths.reports.voicemailReplies,
                    icon: ICONS.voicemailReplies,
                  },
                ]
              : []),
            //...reportsNav,
          ],
        },

        reportsAndDashboardsNav,
        manageAccountNav,
        {
          subheader: 'Help',
          initiallyClosed: true,
          items: [
            {
              title: 'Quick Links',
              path: paths.help.quicklinks,
              icon: ICONS.help,
            },
            ...(!isLegislative
              ? [
                  {
                    title: 'Video Walkthroughs',
                    path: paths.help.trainingVideos,
                    icon: ICONS.trainingVideos,
                  },
                ]
              : []),
          ],
        },
      ];
    }

    if (userRole === 'legislative_member') {
      return [
        votersNav,
        ...(voterCommsMetadata?.aggregate?.aggregate?.count > 0 ? [communicationsNav] : []),
        {
          subheader: 'CAMPAIGNS',
          items: [
            {
              title: 'Working Campaigns',
              path: paths.dashboard.campaigns,
              icon: ICONS.campaigns,
            },
            ...(isLegislative
              ? [
                  {
                    title: 'Voicemail Replies',
                    path: paths.reports.voicemailReplies,
                    icon: ICONS.voicemailReplies,
                  },
                ]
              : []),
          ],
        },
        reportsAndDashboardsNav,
        manageAccountNav,
        {
          subheader: 'Help',
          initiallyClosed: true,
          items: [
            {
              title: 'Quick Links',
              path: paths.help.quicklinks,
              icon: ICONS.help,
            },
          ],
        },
      ];
    }

    if (userRole === 'national_viewer') {
      // Read-only: the National dashboard and the performance reports, the two surfaces
      // EffectiveRoleGuard lets a national_viewer reach. Every report level lives on that one page.
      return [
        nationalNav,
        {
          path: '',
          items: [
            {
              title: 'Performance Reports',
              path: paths.reports.activity,
              icon: ICONS.report,
            },
          ],
        },
      ];
    }

    if (userRole === 'user') {
      // Volunteers get Assigned Tasks (only for an org in WEB_CALLING_ENABLED_ORG_IDS) and voter
      // search -- the two surfaces EffectiveRoleGuard lets them reach.
      return [...callingNav, votersNav];
    }
    return [];
    // selectedOrganizationId is listed so the Assigned Tasks entry follows an org switch between
    // two orgs that share a role and a state -- neither of which would otherwise recompute this.
  }, [userRole, manageAccountNav, voterCommsMetadata, state, selectedOrganizationId]);
}
